// components/ExportButton.tsx
import React, { useState, useEffect } from 'react'
import { Button } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { downloadExport } from '../../utils/fileDownloader'

interface ExportButtonProps {
  exportId: string
  uri?: string
  filename?: string
  className?: string
  showProgress?: boolean
  size?: 'sm' | 'lg'
  children?: React.ReactNode
}

const ExportButton: React.FC<ExportButtonProps> = ({
  exportId,
  uri,
  filename,
  className,
  size,
  children,
}) => {
  const [isDownloading, setIsDownloading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // Use the accessToken from the auth slice
  const accessToken = useSelector((state: RootState) => state.auth.accessToken)
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated)

  // Reset error state when exportId changes
  useEffect(() => {
    setError(null)
  }, [exportId])

  const handleExport = () => {
    if (!isAuthenticated || !accessToken) {
      setError('Authentication required. Please log in again.')
      return
    }

    setIsDownloading(true)
    setProgress(0)
    setError(null)

    downloadExport({
      exportId,
      uri,
      accessToken,
      filename,
      onProgress: (progressValue: number) => {
        setProgress(progressValue)
      },
      onComplete: () => {
        setIsDownloading(false)
        setProgress(100)
      },
      onError: (error: Error) => {
        console.error('Download failed:', error)
        setIsDownloading(false)
        setError(error.message)
      },
    })
  }

  return (
    <div className={className} style={{ position: 'relative' }}>
      <Button
        variant={isDownloading ? 'secondary' : 'primary'}
        onClick={handleExport}
        disabled={!isAuthenticated || isDownloading}
        size={size}
        className="w-100 position-relative overflow-hidden"
        style={
          isDownloading
            ? {
                background: '#2a2a2a',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                color: '#ffffff',
              }
            : undefined
        }
      >
        <span style={{ position: 'relative', zIndex: 2 }}>
          {isDownloading ? `${Math.round(progress)}%` : children || 'Download'}
        </span>
        {isDownloading && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${Math.max(0, Math.min(100, progress))}%`,
              background:
                'linear-gradient(135deg, rgba(102,126,234,0.45) 0%, rgba(118,75,162,0.45) 100%)',
              zIndex: 1,
              transition: 'width 0.15s ease',
            }}
          />
        )}
      </Button>

      {error && <div className="text-danger mt-2 small">{error}</div>}
    </div>
  )
}

export default ExportButton
